import React, { useCallback, useContext, useEffect, useState } from 'react'
import type { GoogleMapProps } from '@react-google-maps/api'
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api'

import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { FailedMapPlaceholder } from '@awaze/design-system.components.map'
import { pxToRems } from '@awaze/design-system.utils.react'
import {
  getBrandConfig,
  getDepositSchemeDisplayAmount,
} from '@awaze/mfe-common.configs.brand'
import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import {
  REPLACE_SEARCH_CRITERIA,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awaze/mfe-common.contexts.search'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
  TmsContextType,
} from '@awaze/mfe-common.contexts.tms'
import { useWishlist } from '@awaze/mfe-common.contexts.wishlist'
import { Regions } from '@awaze/mfe-common.types-and-mocks.one-search-client'
import { ICommonAggregations } from '@awaze/mfe-common.types-and-mocks.site-search-service'

import { MapPin } from 'src/components/MapPin'
import { DEFAULT_MAX_LEVEL_MAP_ZOOM } from 'src/constants'
import { PERFORMANCE_MARKS } from 'src/constants/performance-marks'
import { useAppConfig } from 'src/context/app-config-provider'
import { Assert } from 'src/utils/assert'
import { dataLayerErrorHandler } from 'src/utils/dataLayerErrorHandler'
import { PropertyInfoResponse } from 'src/utils/getPropertyInfo'
import { searchCriteriaComparisonString } from 'src/utils/helpers'

import { getDepositPriceFormattingCallback } from '../../containers/SearchResults/helpers'

import { useGetActiveDepositScheme } from './hooks/useGetActiveDepositScheme'
import { usePropertyInfo } from './hooks/usePropertyInfo'
import { DynamicMapMarker } from './DynamicMap.types'
import {
  buildMapImpressionAndClickPayload,
  getCoordinatesFromBounds,
  removePopularDestinationFeatureIds,
} from './DynamicMap.utils'
import { MapContent } from './MapContent'

import { StyledDynamicMap } from './DynamicMap.styles'

export interface DynamicMapProps {
  /**
   * The Google Maps API Key
   */
  apiKey: string

  /**
   * Brand config
   */
  brand: Brand

  /**
   * Brand for map pin according to theme
   */
  themeBrand: Brand

  /**
   * The default zoom level on map load
   */
  defaultZoom?: number

  /**
   * The ID the map should render with in the DOM
   */
  mapId?: string

  /**
   * The maximum zoom level allowed
   */
  maxZoom?: number

  /**
   * The minium zoom level allowed
   */
  minZoom?: number

  /**
   * The Map aspect ratio
   */
  aspectRatio?: '4:3' | '16:9'

  /**
   * Map translation strings
   */
  translations: {
    failedMapPlaceholderHeading: string
    failedMapPlaceholderText: string
    searchFormMapAreaText: string
  }

  /**
   * ???
   */
  unloadMapOnUnmount?: boolean

  /**
   * Callback to get the property info for a marker
   */
  getPropertyInfo: (propertyId: string) => Promise<PropertyInfoResponse>

  /**
   * The PDP accommodation segment translated
   */
  pdpUrlSegment: string

  /**
   * should map be full height
   */
  fullHeight?: boolean

  /**
   * should wishlist button be visible on Dynamic map micro property card
   */
  wishlistButtonVisible?: boolean
}

export const getWishlistButtonProps = (
  propertyCode: string | undefined,
  isPropertyInWishlist: (propertyId: string) => boolean,
  toggleWishlistItem: (propertyId: string) => void,
  tms: TmsContextType | undefined,
): { isChecked: boolean; onClickCallback: () => void } =>
  propertyCode
    ? {
        isChecked: isPropertyInWishlist(propertyCode.toString()),
        onClickCallback: () => {
          toggleWishlistItem(propertyCode.toString())
          tms?.updateDataLayer(
            'link',
            isPropertyInWishlist(propertyCode.toString())
              ? 'wishlist_click_remove'
              : 'wishlist_click_add',
          )
        },
      }
    : {
        isChecked: false,
        onClickCallback: () => {},
      }

export const DynamicMap: React.FC<DynamicMapProps> = ({
  apiKey,
  brand,
  themeBrand,
  mapId = 'google-map-script',
  unloadMapOnUnmount = true,
  defaultZoom = 7,
  maxZoom = DEFAULT_MAX_LEVEL_MAP_ZOOM,
  minZoom = 3,
  aspectRatio = '4:3',
  fullHeight = false,
  getPropertyInfo,
  pdpUrlSegment,
  translations,
  wishlistButtonVisible = false,
}) => {
  const { ASSET_PREFIX } = useAppConfig()
  const tms = useContext(TmsContext)
  const { coordinates, searchCriteria, aggregations, rhsLevel, total } =
    useSearchContextState()

  // TODO: Correct types in MFE-Common and remove this hack - the
  // searchContext will error before it can possbily return this as undefined,
  // so it shouldn't be an optional property. If it *was* there would be a runtime error
  // in MFE-Common so may as well throw an error here instead
  Assert.defined(searchCriteria, 'SearchCriteria must be defined')

  const isDatedSearch = Boolean(searchCriteria?.start)
  const aspectRatioArray: Array<number> = aspectRatio
    .split(':')
    .map((val) => parseInt(val))
  const mapHeight = `${(aspectRatioArray[1] / aspectRatioArray[0]) * 100}%`
  const [map, setMap] = useState<google.maps.Map | null>(null)

  const [markers, setMarkers] = useState<Array<DynamicMapMarker> | undefined>(
    [],
  )

  const [isSetBoundsComplete, setBoundsComplete] = useState(false)
  const [userInteracted, setUserInteracted] = useState<boolean>(false)
  const [isZoomEventLocked, setIsZoomEventLocked] = useState(true)
  const [googleMapHasSetInitialBounds, setGoogleMapHasSetInitialBounds] =
    useState(false)
  const [isInitialLoadFinished, setIsInitialLoadFinished] = useState(false)

  const {
    currencySymbol,
    formatCurrencyToObject,
    formatMessage,
    language,
    country,
  } = useIntlContext()
  const dispatch = useSearchContextDispatch()
  const searchCriteriaWithoutNESW = {
    ...searchCriteria,
    ne: null,
    sw: null,
  }
  const searchCriteriaWithoutNESWString = searchCriteriaComparisonString(
    searchCriteriaWithoutNESW,
  )

  const { isLoaded, loadError } = useJsApiLoader({
    id: mapId,
    googleMapsApiKey: apiKey,
    language: language,
    region: country,
  })

  const deposits = getBrandConfig(brand).deposits

  Assert.defined(deposits, 'Deposits must be defined!')

  useEffect(() => {
    if ((markers?.length ?? 0) > 0 && googleMapHasSetInitialBounds) {
      setBoundsComplete(true)
    }
  }, [markers?.length, googleMapHasSetInitialBounds])

  useEffect(() => {
    if (!isInitialLoadFinished && isSetBoundsComplete) {
      setIsInitialLoadFinished(true)
    }
  }, [isSetBoundsComplete])

  useEffect(() => {
    if (map && isLoaded && (coordinates?.length ?? 0) > 0) {
      const bounds = new window.google.maps.LatLngBounds()

      coordinates?.forEach((coordinate) => bounds.extend(coordinate))

      if (!isSetBoundsComplete) {
        map.fitBounds(bounds)
      }

      setMarkers(coordinates)
    }
  }, [coordinates, isLoaded, map])

  useEffect(() => {
    if (isInitialLoadFinished) {
      setIsZoomEventLocked(false)
    }
  }, [isInitialLoadFinished])

  useEffect(() => {
    new DataLayerFactory().getEvent(DataLayerEventType.MapView).update(
      tms,
      // TODO correct types in MFE-Common - runtime code handles undefined fine
      // so the 'aggregations' property should be ICommonAggregations | undefined
      // ...the runtime code *handles* undefined values fine
      { searchCriteria, aggregations: aggregations as ICommonAggregations },
      dataLayerErrorHandler,
    )
  }, [searchCriteriaWithoutNESWString])

  const {
    activeMarkerId,
    propertyInfo,
    isPropertyInfoError,
    onMarkerClick,
    setActiveMarkerId,
  } = usePropertyInfo({
    brand,
    getPropertyInfo,
    isDatedSearch,
  })

  const { activeDeposit } = useGetActiveDepositScheme({
    brand,
    country,
    deposits,
    propertyInfo,
  })
  const showOfferBanner = activeDeposit && propertyInfo
  const depositDisplayAmount =
    activeDeposit &&
    getDepositSchemeDisplayAmount(
      activeDeposit,
      currencySymbol,
      getDepositPriceFormattingCallback(brand, formatCurrencyToObject),
    )
  const showDepositTerms =
    showOfferBanner && propertyInfo?.cardinality === 'MULTI'

  useEffect(() => {
    if (!propertyInfo) {
      return
    }

    new DataLayerFactory()
      .getEvent(DataLayerEventType.MapProductImpression)
      .update(
        tms,
        buildMapImpressionAndClickPayload(
          searchCriteria,
          aggregations as ICommonAggregations,
          rhsLevel as Regions,
          total as number,
          propertyInfo,
        ),
        dataLayerErrorHandler,
      )
  }, [propertyInfo])

  const containerStyle = {
    width: '100%',
    height: fullHeight ? '100%' : 0,
    paddingTop: mapHeight,
    borderRadius: fullHeight ? 0 : pxToRems(8),
  }

  const onLoad: GoogleMapProps['onLoad'] = useCallback(
    async (googleMapsClass: google.maps.Map) => {
      const bounds = new window.google.maps.LatLngBounds()

      googleMapsClass.fitBounds(bounds)

      setMap(googleMapsClass)
    },
    [],
  )

  const onDragEnd: GoogleMapProps['onDragEnd'] = useCallback(() => {
    setUserInteracted(true)
  }, [])

  const onZoomChanged: GoogleMapProps['onZoomChanged'] = useCallback(() => {
    if (!isZoomEventLocked) {
      setUserInteracted(true)
    }
  }, [isZoomEventLocked])

  const onBoundsChanged: GoogleMapProps['onBoundsChanged'] = useCallback(() => {
    if (!isInitialLoadFinished) {
      setGoogleMapHasSetInitialBounds(true)
    }
  }, [isInitialLoadFinished])

  const onIdle: GoogleMapProps['onIdle'] = useCallback(async () => {
    if (map) {
      const bounds = map?.getBounds()
      const shouldTriggerNewSearch =
        isSetBoundsComplete && bounds && userInteracted

      if (shouldTriggerNewSearch) {
        const coordinates = getCoordinatesFromBounds(bounds)
        const ne = `${coordinates.ne.lat}, ${coordinates.ne.lon}`
        const sw = `${coordinates.sw.lat}, ${coordinates.sw.lon}`

        const fullSearchCriteria = {
          ...searchCriteria,
          ...(ne && { ne }),
          ...(sw && { sw }),
          regionName: translations.searchFormMapAreaText,
        }

        setUserInteracted(false)

        // TODO: GD-234 - Leaving this here for now, but this should be removed once the typing for SearchCriteria no longer inccludes regionId
        delete fullSearchCriteria.regionId
        delete fullSearchCriteria.placesId
        delete fullSearchCriteria.radiusCentre
        delete fullSearchCriteria.radiusKm

        if (
          fullSearchCriteria.features &&
          fullSearchCriteria.features.length > 0
        ) {
          fullSearchCriteria.features = removePopularDestinationFeatureIds(
            fullSearchCriteria.features,
          )
        }

        if (
          fullSearchCriteria.siteFeatures &&
          fullSearchCriteria.siteFeatures.length > 0
        ) {
          fullSearchCriteria.siteFeatures = removePopularDestinationFeatureIds(
            fullSearchCriteria.siteFeatures,
          )
        }

        dispatch({
          type: REPLACE_SEARCH_CRITERIA,
          searchCriteria: fullSearchCriteria,
        })
      }
    }
  }, [searchCriteria, map, isSetBoundsComplete, userInteracted])

  const onUnmount = useCallback(function callback() {
    if (unloadMapOnUnmount) {
      setMap(null)
    }
  }, [])

  const onTilesLoaded: GoogleMapProps['onTilesLoaded'] = useCallback(() => {
    if (map) {
      window?.performance &&
        window.performance.mark(PERFORMANCE_MARKS.MAP_TILES_LOADED)
    }
  }, [map])

  const { isPropertyInWishlist, toggleWishlistItem } = useWishlist()
  const propertyCode = propertyInfo?.code
  const wishlistButtonProps = getWishlistButtonProps(
    propertyCode,
    isPropertyInWishlist,
    toggleWishlistItem,
    tms,
  )

  if (loadError) {
    const errorReason = loadError.message ?? ''
    const errorCode = loadError.name ?? ''

    new DataLayerFactory().getEvent(DataLayerEventType.MapViewFail).update(
      tms,
      // TODO update types in MFE-Common (see above)
      {
        searchCriteria,
        aggregations: aggregations as ICommonAggregations,
        errorReason,
        errorCode,
      },
      dataLayerErrorHandler,
    )

    return (
      <FailedMapPlaceholder
        containerStyle={containerStyle}
        errorLabels={{
          heading: formatMessage({
            id: 'map.error.heading',
            defaultMessage: 'Oops, something went wrong',
          }),
          text: formatMessage({
            id: 'map.error.text',
            defaultMessage: `We couldn't load the map right now. Please try and refresh the page`,
          }),
        }}
      />
    )
  }

  return isLoaded ? (
    <StyledDynamicMap>
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={defaultZoom}
        onIdle={onIdle}
        onDragEnd={onDragEnd}
        onZoomChanged={onZoomChanged}
        onBoundsChanged={onBoundsChanged}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onTilesLoaded={onTilesLoaded}
        options={{
          minZoom,
          maxZoom,
          mapTypeControl: false,
          streetViewControl: false,
          styles: [
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }],
            },
          ],
        }}
      >
        {markers &&
          Array.isArray(markers) &&
          markers.map((marker) => {
            const showInfoWindow =
              activeMarkerId && activeMarkerId === marker.id

            return (
              <Marker
                icon={{
                  url: MapPin(themeBrand, ASSET_PREFIX),
                  scaledSize: new google.maps.Size(27, 35, 'px', 'px'),
                }}
                key={`marker_${marker.id}_${marker.lat}|${marker.lng}`}
                onClick={() => onMarkerClick(marker.id)}
                position={marker}
              >
                {showInfoWindow && (
                  <InfoWindow
                    position={marker}
                    options={{
                      disableAutoPan: true,
                    }}
                    onLoad={(thisWindow) => {
                      thisWindow.setOptions({ disableAutoPan: false })
                    }}
                    onCloseClick={() => setActiveMarkerId(null)}
                  >
                    <MapContent
                      wishlistButtonVisible={wishlistButtonVisible}
                      showOfferBanner={showOfferBanner}
                      isDatedSearch={isDatedSearch}
                      wishlistButtonProps={wishlistButtonProps}
                      pdpUrlSegment={pdpUrlSegment}
                      depositDisplayAmount={depositDisplayAmount}
                      showDepositTerms={showDepositTerms}
                      propertyInfo={propertyInfo}
                      isPropertyInfoError={isPropertyInfoError}
                    />
                  </InfoWindow>
                )}
              </Marker>
            )
          })}
      </GoogleMap>
    </StyledDynamicMap>
  ) : (
    <></>
  )
}
